import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Stack
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

export interface KioskPaymentSettings {
  defaultAccountId?: number;
  defaultAccountName?: string;
  defaultBankAccountId?: number;
  defaultBankAccountName?: string;
  defaultBankSubAccountId?: number;
  defaultBankSubAccountName?: string;
  defaultWarehouseId?: string;
  defaultWarehouseName?: string;
}

interface KioskPaymentSettingsTabProps {
  settings: KioskPaymentSettings;
  onSettingsChange: (settings: KioskPaymentSettings) => void;
  companyId: string | null;
}

interface AccountOption {
  id: number;
  companyName: string;
  accountCode: string;
  isGenelCari: boolean;
}

interface BankAccountOption {
  id: number;
  bankName: string;
  branchName: string;
  isPosAccount: boolean;
  subAccounts: BankSubAccountOption[];
}

interface BankSubAccountOption {
  id: number;
  accountName: string;
  isPosAccount: boolean;
}

interface WarehouseOption {
  id: string;
  name: string;
  isDefault: boolean;
}

const KioskPaymentSettingsTab: React.FC<KioskPaymentSettingsTabProps> = ({
  settings,
  onSettingsChange,
  companyId
}) => {
  const { t } = useTranslation('salesSystem');
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccountOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Tüm verileri paralel yükle
  useEffect(() => {
    if (!companyId) return;

    const loadAll = async () => {
      setLoading(true);

      try {
        const [accountsRes, banksRes, warehousesRes] = await Promise.allSettled([
          accountingApi.get(`/api/Account`),
          accountingApi.get(`/api/BankAccount`),
          accountingApi.get(`/api/Inventory/warehouses`),
        ]);

        // ── Cari Hesaplar ──
        if (accountsRes.status === 'fulfilled') {
          const result = accountsRes.value.data;
          const raw = result.data || result;
          const list: AccountOption[] = (Array.isArray(raw) ? raw : [])
            .filter((d: any) => d.isActive !== false)
            .map((d: any) => ({
              id: typeof d.id === 'string' ? parseInt(d.id) : d.id,
              companyName: d.companyName || '',
              accountCode: d.accountCode || '',
          isGenelCari: d.companyName === t('kioskPaymentTab.defaultGeneralAccountName'),
            }));
          setAccounts(list);

          // Henüz seçim yoksa "Genel Cari"yi varsayılan yap
          if (!settings.defaultAccountId) {
            const genelCari = list.find(a => a.isGenelCari);
            if (genelCari) {
              onSettingsChange({
                ...settings,
                defaultAccountId: genelCari.id,
                defaultAccountName: genelCari.companyName,
              });
            }
          }
        }

        // ── Banka Hesapları ──
        if (banksRes.status === 'fulfilled') {
          const result = banksRes.value.data;
          const raw = result.data || result;
          const list: BankAccountOption[] = (Array.isArray(raw) ? raw : [])
            .filter((d: any) => d.isActive !== false)
            .map((d: any) => ({
              id: typeof d.id === 'string' ? parseInt(d.id) : d.id,
              bankName: d.bankName || '',
              branchName: d.branchName || '',
              isPosAccount: d.isPosAccount || false,
              subAccounts: (Array.isArray(d.subAccounts) ? d.subAccounts : [])
                .filter((s: any) => s.isActive !== false)
                .map((s: any) => ({
                  id: typeof s.id === 'string' ? parseInt(s.id) : s.id,
                  accountName: s.accountName || '',
                  isPosAccount: s.isPosAccount || false,
                })),
            }));
          setBankAccounts(list);
        }

        // ── Depolar ──
        if (warehousesRes.status === 'fulfilled') {
          const result = warehousesRes.value.data;
          const raw = result.data || result;
          const list: WarehouseOption[] = (Array.isArray(raw) ? raw : [])
            .filter((d: any) => d.isActive !== false && d.isDeleted !== true)
            .map((d: any) => ({
              id: (d.id || d.Id)?.toString() || '',
              name: d.name || d.Name || d.warehouseName || '',
              isDefault: d.isDefault || d.IsDefault || false,
            }));
          setWarehouses(list);

          // Henüz seçim yoksa varsayılan depoyu seç
          if (!settings.defaultWarehouseId) {
            const defaultW = list.find(w => w.isDefault) || (list.length === 1 ? list[0] : null);
            if (defaultW) {
              onSettingsChange({
                ...settings,
                defaultWarehouseId: defaultW.id,
                defaultWarehouseName: defaultW.name,
              });
            }
          }
        }
      } catch (error) {
        console.error('Ödeme ayarları verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // POS hesaplarını filtrele (isPosAccount true olan ana + alt hesaplar)
  const posAccounts = useMemo(() => {
    const result: BankAccountOption[] = [];
    bankAccounts.forEach(bank => {
      // Ana hesap POS ise ekle
      if (bank.isPosAccount) {
        result.push(bank);
      }
      // Alt hesaplardan POS olanlar varsa ana hesabı da dahil et (eğer yoksa)
      else if (bank.subAccounts.some(s => s.isPosAccount)) {
        result.push(bank);
      }
    });
    return result;
  }, [bankAccounts]);

  // Seçili banka hesabının alt hesapları (sadece POS olanlar)
  const selectedBankSubAccounts = useMemo(() => {
    const bank = bankAccounts.find(b => b.id === settings.defaultBankAccountId);
    if (!bank?.subAccounts?.length) return [];
    return bank.subAccounts.filter(s => s.isPosAccount || !bank.isPosAccount);
  }, [bankAccounts, settings.defaultBankAccountId]);

  // Durum: Tüm ayarlar tamam mı?
  const isComplete = !!(settings.defaultAccountId && settings.defaultBankAccountId && settings.defaultWarehouseId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t('kioskPaymentTab.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Durum Bilgisi */}
      <Alert
        severity={isComplete ? 'success' : 'warning'}
        icon={isComplete ? <CheckCircleIcon /> : undefined}
        sx={{ mb: 2, py: 0.5 }}
      >
        {isComplete
          ? t('kioskPaymentTab.status.complete')
          : t('kioskPaymentTab.status.incomplete')}
      </Alert>

      {/* 3 Alan Yan Yana Grid */}
      <Stack spacing={2}>
        {/* 1. Cari Hesap */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600}>{t('kioskPaymentTab.sections.defaultAccount')}</Typography>
          </Stack>
          <Autocomplete
            size="small"
            options={accounts}
            getOptionLabel={(option) => option.companyName}
            value={accounts.find(a => a.id === settings.defaultAccountId) || null}
            onChange={(_, newValue) => {
              onSettingsChange({
                ...settings,
                defaultAccountId: newValue?.id,
                defaultAccountName: newValue?.companyName,
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {option.companyName}
                  </Typography>
                  {option.isGenelCari && (
                    <Chip label={t('kioskPaymentTab.badges.default')} size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('kioskPaymentTab.placeholders.selectAccount')}
                helperText={t('kioskPaymentTab.helpers.generalAccount')}
              />
            )}
            noOptionsText={t('kioskPaymentTab.noOptions.account')}
          />
        </Box>

        {/* 2. POS Banka Hesabı */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <AccountBalanceIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600}>{t('kioskPaymentTab.sections.posBankAccount')}</Typography>
          </Stack>
          <Autocomplete
            size="small"
            options={posAccounts.length > 0 ? posAccounts : bankAccounts}
            getOptionLabel={(option) =>
              option.branchName
                ? `${option.bankName} - ${option.branchName}`
                : option.bankName
            }
            value={bankAccounts.find(b => b.id === settings.defaultBankAccountId) || null}
            onChange={(_, newValue) => {
              onSettingsChange({
                ...settings,
                defaultBankAccountId: newValue?.id,
                defaultBankAccountName: newValue ? (newValue.branchName ? `${newValue.bankName} - ${newValue.branchName}` : newValue.bankName) : undefined,
                defaultBankSubAccountId: undefined,
                defaultBankSubAccountName: undefined,
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {option.bankName}{option.branchName ? ` - ${option.branchName}` : ''}
                  </Typography>
                  {option.isPosAccount && (
                    <Chip label={t('kioskPaymentTab.badges.pos')} size="small" color="success" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('kioskPaymentTab.placeholders.selectBankAccount')}
                helperText={
                  posAccounts.length > 0
                    ? t('kioskPaymentTab.helpers.posAccountCount', { count: posAccounts.length })
                    : t('kioskPaymentTab.helpers.allBankAccountsListed')
                }
              />
            )}
            noOptionsText={t('kioskPaymentTab.noOptions.bankAccount')}
          />

          {/* Alt Hesap */}
          {selectedBankSubAccounts.length > 0 && (
            <Autocomplete
              size="small"
              sx={{ mt: 1 }}
              options={selectedBankSubAccounts}
              getOptionLabel={(option) => option.accountName}
              value={selectedBankSubAccounts.find(s => s.id === settings.defaultBankSubAccountId) || null}
              onChange={(_, newValue) => {
                onSettingsChange({
                  ...settings,
                  defaultBankSubAccountId: newValue?.id,
                  defaultBankSubAccountName: newValue?.accountName,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('kioskPaymentTab.placeholders.selectSubAccountOptional')}
                />
              )}
              noOptionsText={t('kioskPaymentTab.noOptions.subAccount')}
            />
          )}
        </Box>

        {/* 3. Depo */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <WarehouseIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600}>{t('kioskPaymentTab.sections.defaultWarehouse')}</Typography>
          </Stack>
          <Autocomplete
            size="small"
            options={warehouses}
            getOptionLabel={(option) => option.name}
            value={warehouses.find(w => w.id === settings.defaultWarehouseId) || null}
            onChange={(_, newValue) => {
              onSettingsChange({
                ...settings,
                defaultWarehouseId: newValue?.id,
                defaultWarehouseName: newValue?.name,
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                  {option.isDefault && (
                    <Chip label={t('kioskPaymentTab.badges.default')} size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('kioskPaymentTab.placeholders.selectWarehouse')}
                helperText={t('kioskPaymentTab.helpers.warehouseDeductionInfo')}
              />
            )}
            noOptionsText={t('kioskPaymentTab.noOptions.warehouse')}
          />
        </Box>
      </Stack>

      {/* Kompakt Özet */}
      {isComplete && (
        <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t('kioskPaymentTab.summary.account')}:</strong> {settings.defaultAccountName}
            {' | '}
            <strong>{t('kioskPaymentTab.summary.bank')}:</strong> {settings.defaultBankAccountName}
            {settings.defaultBankSubAccountName ? ` / ${settings.defaultBankSubAccountName}` : ''}
            {' | '}
            <strong>{t('kioskPaymentTab.summary.warehouse')}:</strong> {settings.defaultWarehouseName}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KioskPaymentSettingsTab;
